/**
 * The local service API (docs/api.md). `/api` is proxied by Vite to 127.0.0.1:4320.
 * Errors are `{ error: { code, message } }` with a user-facing British English message.
 */
import type { ExportRecord, Project, CheckReceipt } from '@/domain';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function readError(res: Response): Promise<ApiError> {
  let code = `http-${res.status}`;
  let message = res.status === 0 ? 'The local service did not answer.' : `The local service answered ${res.status}.`;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    if (body?.error?.message) message = body.error.message;
    if (body?.error?.code) code = body.error.code;
  } catch { /* not JSON */ }
  return new ApiError(res.status, code, message);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<{ data: T; res: Response }> {
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    throw new ApiError(0, 'unreachable', 'The local service is not running. Start it with npm run dev.');
  }
  if (!res.ok) throw await readError(res);
  const text = await res.text();
  return { data: (text ? JSON.parse(text) : null) as T, res };
}
const json = (body: unknown, extra: Record<string, string> = {}): RequestInit => ({
  method: 'POST', headers: { 'content-type': 'application/json', ...extra }, body: JSON.stringify(body),
});

export interface Health { ok: boolean; version: string; intakeMode: string; demo?: boolean }
export interface DemoPhoto { id: string; title: string; family: string | null; url: string; /** The published product text the reading was recorded with (its sizes become published facts). */ text: string }
export interface ProfilesResponse { stock: unknown; construction: unknown; appearance: unknown }
export interface ProjectSummary { id: string; name: string; updatedAt: string; revisionCount: number }
export interface UploadedImage { imageId: string; sha256: string; width: number; height: number; path: string; thumbPath: string }
export type ProjectFrom = 'blank' | 'preset:even' | 'preset:hylla' | 'preset:residual';
/** The service returns the project plus its id (the directory name). */
export type ServerProject = Project & { id?: string };

export const api = {
  health: () => request<Health>('/api/health').then((r) => r.data),
  profiles: () => request<ProfilesResponse>('/api/profiles').then((r) => r.data),
  listProjects: () => request<ProjectSummary[]>('/api/projects').then((r) => r.data),
  createProject: (name: string, from: ProjectFrom = 'blank') =>
    request<ServerProject>('/api/projects', json({ name, from })).then((r) => ({ project: r.data, etag: r.res.headers.get('etag'), location: r.res.headers.get('location') })),
  getProject: (id: string) =>
    request<ServerProject>(`/api/projects/${encodeURIComponent(id)}`).then((r) => ({ project: r.data, etag: r.res.headers.get('etag') })),
  putProject: (id: string, project: Project, etag: string | null, { keepalive = false }: { keepalive?: boolean } = {}) =>
    request<ServerProject>(`/api/projects/${encodeURIComponent(id)}`, {
      method: 'PUT', keepalive,
      headers: { 'content-type': 'application/json', ...(etag ? { 'if-match': etag } : {}) },
      body: JSON.stringify(project),
    }).then((r) => ({ project: r.data, etag: r.res.headers.get('etag') })),
  uploadImage: (id: string, file: Blob, filename = 'photo.jpg') => {
    const fd = new FormData();
    fd.append('file', file, filename);
    return request<UploadedImage>(`/api/projects/${encodeURIComponent(id)}/images`, { method: 'POST', body: fd }).then((r) => r.data);
  },
  imageUrl: (id: string, imageId: string) => `/api/projects/${encodeURIComponent(id)}/images/${encodeURIComponent(imageId)}`,
  thumbUrl: (id: string, imageId: string) => `/api/projects/${encodeURIComponent(id)}/images/${encodeURIComponent(imageId)}/thumb`,
  startIntake: (id: string, body: { imageIds: string[]; url: string; pastedText: string }) =>
    request<{ runId: string }>(`/api/projects/${encodeURIComponent(id)}/intake`, json(body)).then((r) => r.data),
  cancelIntake: (runId: string) => request<unknown>(`/api/intake/${encodeURIComponent(runId)}/cancel`, { method: 'POST' }).catch(() => null),
  intakeEventsUrl: (runId: string) => `/api/intake/${encodeURIComponent(runId)}/events`,
  /**
   * Does the service still hold this analysis run? Probes the events URL (which replays the run)
   * and drops the body: true (it answers), false (404: the run is gone), null (no answer yet).
   */
  intakeRunExists: async (runId: string): Promise<boolean | null> => {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 5000);
    try {
      const res = await fetch(`/api/intake/${encodeURIComponent(runId)}/events`, { signal: ctl.signal, headers: { accept: 'text/event-stream' } });
      if (res.status === 404) return false;
      return res.ok ? true : null;
    } catch {
      return null;
    } finally {
      clearTimeout(t);
      ctl.abort();
    }
  },
  postExport: (id: string, body: { revisionId: string; manufacturingHash: string; kind: 'csv' | 'json' | 'pdf'; filename: string; content?: string }) =>
    request<ExportRecord>(`/api/projects/${encodeURIComponent(id)}/exports`, json(body)).then((r) => r.data),
  /** Demo mode: the prepared photos, each with a recorded reading. */
  demoPhotos: () => request<{ photos: DemoPhoto[] }>('/api/demo/photos').then((r) => r.data.photos).catch(() => [] as DemoPhoto[]),
  /** Run the independent check on a saved revision (the service rebuilds it itself). */
  checkRevision: (id: string, revisionId: string) =>
    request<CheckReceipt>(`/api/projects/${encodeURIComponent(id)}/revisions/${encodeURIComponent(revisionId)}/check`, { method: 'POST' }).then((r) => r.data),
};

/**
 * Subscribe to a Server-Sent Events stream with named events. Returns a close function. Any
 * error (a dropped or refused connection) closes the stream and calls `onError` once, so the
 * caller decides how to reconnect instead of the browser retrying silently forever (HD-11).
 */
export function subscribeEvents(url: string, handlers: Record<string, (data: unknown) => void>, onError?: () => void): () => void {
  const es = new EventSource(url);
  for (const [name, fn] of Object.entries(handlers)) {
    es.addEventListener(name, (ev) => {
      // the browser's own connection `error` (not a MessageEvent) is not the service's `error` event
      if (!(ev instanceof MessageEvent)) return;
      let data: unknown = null;
      try { data = JSON.parse((ev as MessageEvent<string>).data); } catch { data = (ev as MessageEvent<string>).data; }
      fn(data);
    });
  }
  let failed = false;
  es.onerror = () => {
    if (failed) return;
    failed = true;
    es.close();
    onError?.();
  };
  return () => { failed = true; es.close(); };
}
